/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { ExternalValueListCreateCommandV1 } from '../models/external-value-list-create-command-v-1';
import { ExternalValueListIntegrationV1 } from '../models/external-value-list-integration-v-1';
import { ExternalValueListPushJobIntegrationV1 } from '../models/external-value-list-push-job-integration-v-1';


/**
 * API for managing and pushing external value lists via integration.
 */
@Injectable({
  providedIn: 'root',
})
export class V1ValueListIntegrationService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation createExternalValueListIntegrationV1
   */
  static readonly CreateExternalValueListIntegrationV1Path = '/v1/integration/value-lists';

  /**
   * Create a new external value list.
   *
   * Provisions a list's metadata (listKey, display name) so values can be pushed into it via the push endpoint below.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createExternalValueListIntegrationV1()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createExternalValueListIntegrationV1$Response(params: {
    body: ExternalValueListCreateCommandV1
  },
  context?: HttpContext

): Observable<StrictHttpResponse<ExternalValueListIntegrationV1>> {

    const rb = new RequestBuilder(this.rootUrl, V1ValueListIntegrationService.CreateExternalValueListIntegrationV1Path, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ExternalValueListIntegrationV1>;
      })
    );
  }

  /**
   * Create a new external value list.
   *
   * Provisions a list's metadata (listKey, display name) so values can be pushed into it via the push endpoint below.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createExternalValueListIntegrationV1$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createExternalValueListIntegrationV1(params: {
    body: ExternalValueListCreateCommandV1
  },
  context?: HttpContext

): Observable<ExternalValueListIntegrationV1> {

    return this.createExternalValueListIntegrationV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<ExternalValueListIntegrationV1>) => r.body as ExternalValueListIntegrationV1)
    );
  }

  /**
   * Path part for operation pushExternalValueListV1
   */
  static readonly PushExternalValueListV1Path = '/v1/integration/value-lists/{listKey}/push';

  /**
   * Push a full replacement set of values for a list.
   *
   * Accepts a CSV file (columns: value, default, text:<locale>..., tag:<key>...) and replaces the list's values asynchronously. Returns immediately with a job id to poll for status.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `pushExternalValueListV1()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  pushExternalValueListV1$Response(params: {
    listKey: string;
    body?: {
'file': Blob;
}
  },
  context?: HttpContext

): Observable<StrictHttpResponse<ExternalValueListPushJobIntegrationV1>> {

    const rb = new RequestBuilder(this.rootUrl, V1ValueListIntegrationService.PushExternalValueListV1Path, 'post');
    if (params) {
      rb.path('listKey', params.listKey, {});
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ExternalValueListPushJobIntegrationV1>;
      })
    );
  }

  /**
   * Push a full replacement set of values for a list.
   *
   * Accepts a CSV file (columns: value, default, text:<locale>..., tag:<key>...) and replaces the list's values asynchronously. Returns immediately with a job id to poll for status.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `pushExternalValueListV1$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  pushExternalValueListV1(params: {
    listKey: string;
    body?: {
'file': Blob;
}
  },
  context?: HttpContext

): Observable<ExternalValueListPushJobIntegrationV1> {

    return this.pushExternalValueListV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<ExternalValueListPushJobIntegrationV1>) => r.body as ExternalValueListPushJobIntegrationV1)
    );
  }

  /**
   * Path part for operation getExternalValueListPushJobStatusV1
   */
  static readonly GetExternalValueListPushJobStatusV1Path = '/v1/integration/value-lists/{listKey}/push-jobs/{jobId}';

  /**
   * Get the status of a value list push job.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getExternalValueListPushJobStatusV1()` instead.
   *
   * This method doesn't expect any request body.
   */
  getExternalValueListPushJobStatusV1$Response(params: {
    listKey: string;
    jobId: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<ExternalValueListPushJobIntegrationV1>> {

    const rb = new RequestBuilder(this.rootUrl, V1ValueListIntegrationService.GetExternalValueListPushJobStatusV1Path, 'get');
    if (params) {
      rb.path('listKey', params.listKey, {});
      rb.path('jobId', params.jobId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<ExternalValueListPushJobIntegrationV1>;
      })
    );
  }

  /**
   * Get the status of a value list push job.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getExternalValueListPushJobStatusV1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getExternalValueListPushJobStatusV1(params: {
    listKey: string;
    jobId: string;
  },
  context?: HttpContext

): Observable<ExternalValueListPushJobIntegrationV1> {

    return this.getExternalValueListPushJobStatusV1$Response(params,context).pipe(
      map((r: StrictHttpResponse<ExternalValueListPushJobIntegrationV1>) => r.body as ExternalValueListPushJobIntegrationV1)
    );
  }

}
